<script setup>
import { ref, watch, computed, inject } from 'vue'
import { useAnomalyDetailsStore } from '@/store/anomalyDetails'
import { useRoute } from 'vue-router'

const route = useRoute()
const loading = computed(() => { return anomalyDetailsStore.loadingStuff })
const anomalyDetailsStore = useAnomalyDetailsStore()
const showDetailModal = ref(false)
const tabContent = ref({})
const tabValue = ref('0')
const errors = computed(() => {
  return tabValue.value === '1' || anomalyDetailsStore.errors[0].formattedEndDate ? anomalyDetailsStore.errors : []
})

watch(() => route.params.anomalyId, anomalyDetailsStore.fetchAnomalyDetails, { immediate: true })

const openDetailModal = (errorLog) => {
  tabContent.value = errorLog.details
  showDetailModal.value = true
}

const getLabelButton = () => {
  return anomalyDetailsStore.corruptedFields.find(el => el.children.find(el => el.data.action)) ? 'Submit' : 'Check'
}

const getIconButton = () => {
  return anomalyDetailsStore.corruptedFields.find(el => el.children.find(el => el.data.action)) ? 'pi pi-save' : 'pi pi-refresh'
}

const windowIsTooSmall = inject('windowTooSmall')
</script>

<template>
  <Dialog
    v-model:visible="showDetailModal"
    :position="windowIsTooSmall ? 'center' : 'right'"
    :dismissable-mask="true"
    content-style="overflow-y: hidden; display: flex; flex-direction: column"
    modal
    header="Error Detail"
    :style="{maxWidth: windowIsTooSmall ? '90vw' : '60vw', minWidth: '35vw', maxHeight: windowIsTooSmall ? '90vh' : '60vh' }"
  >
    <div
      class="w-full flex flex-col"
    >
      <highlightjs
        language="json"
        :code="JSON.stringify(tabContent, null, 2)"
        class="overflow-y-auto h-auto"
      />
    </div>
  </Dialog>
  <div class="card relative">
    <div :style="{position: 'absolute', left:'0px', top: '0px'}">
      <router-link
        to="/anomalies"
      >
        <Avatar
          icon="pi pi-angle-left"
          size="large"
          :style="{backgroundColor: '#6366F1', color: 'white', cursor: 'pointer',borderBottomLeftRadius: 0, borderTopRightRadius: 0,borderTopLeftRadius: windowIsTooSmall ? 0 : undefined }"
        />
      </router-link>
    </div>
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 min-h-0 -mx-4 md:mx-0 md:px-4 gap-4 rounded-lg bg-[--p-content-background] flex flex-col"
    >
      <div class="flex items-center gap-4 border-b border-b-[#e0e3e8] dark:border-b-gray-600">
        <i
          class="pi pi-exclamation-triangle text-danger pl-4 h-16 w-16"
          :class="{'text-red-500': !anomalyDetailsStore.errors[0].formattedEndDate, 'text-green-500': anomalyDetailsStore.errors[0].formattedEndDate}"
          style="font-size: 56px"
        />
        <Timeline
          class="anomaly-timeline pb-4"
          :value="[{date: anomalyDetailsStore.errors[0].startdate.toLocaleString(), status: 'Start', duration:anomalyDetailsStore.errors[0].formattedDuration, objects: anomalyDetailsStore.corruptedObjects } , {date:anomalyDetailsStore.errors[0].formattedEndDate, status: 'End' } ]"
        >
          <template #opposite="slotProps">
            <small class="text-surface-500 dark:text-surface-400">{{ slotProps.item.status }}</small>
          </template>
          <template #content="slotProps">
            <div class="flex flex-col gap-5">
              {{ slotProps.item.date }}
              <div
                v-if="slotProps.item.objects"
                class="flex flex-col  pb-5"
              >
                <p class=" font-bold">
                  <i
                    class="pi pi-stopwatch"
                  />
                  {{ slotProps.item.duration }}
                </p>
                <p class="pt-4">
                  <Tag
                    :icon="anomalyDetailsStore.errors[0].formattedEndDate.length ? 'pi pi-check': 'pi pi-exclamation-circle'"
                    :severity="anomalyDetailsStore.errors[0].formattedEndDate.length ? 'success': 'danger'"
                    :value="anomalyDetailsStore.errors[0].formattedEndDate.length ? 'RESOLVED': 'ONGOING'"
                  />
                </p>
                <p> {{ '' }}</p>
              </div>
            </div>
          </template>
          <template #connector>
            <div
              class="p-timeline-event-connector"
              :style="{ height: '10px', backgroundColor: anomalyDetailsStore.errors[0].formattedEndDate ? '#15803D' : '#B91C1C' }"
            />
          </template>
          <template #marker>
            <div
              class="p-timeline-event-marker"
              :style="{backgroundColor: 'white', borderColor:anomalyDetailsStore.errors[0].formattedEndDate ? '#15803D' : '#B91C1C' }"
            />
          </template>
        </Timeline>
        <div
          v-if="!windowIsTooSmall"
          class="flex items-start h-full flex-col flex-grow gap-2"
        >
          <strong>Impacted objects:</strong>
          <div class="flex-grow max-h-[120px] overflow-y-auto">
            {{ anomalyDetailsStore.corruptedObjects }}
          </div>
        </div>
      </div>

      <Tabs
        :value="tabValue"
        class="h-full flex"
      >
        <TabList v-if="anomalyDetailsStore.errors[0].formattedEndDate">
          <Tab
            value="0"
          >
            Errors
          </Tab>
        </TabList>
        <TabList v-else>
          <Tab
            value="0"
            @click="tabValue = '0'"
          >
            Fields With Issues
          </Tab>
          <Tab
            value="1"
            @click="tabValue = '1'"
          >
            Errors
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel
            :value="anomalyDetailsStore.errors[0].formattedEndDate ? '1' : '0'"
          >
            <div
              class="flex-1 overflow-y-auto px-0 -mx-4 md:mx-0 md:pb-4 rounded-lg bg-[--p-content-background] h-full"
              :class="{'py-2': !windowIsTooSmall}"
            >
              <TreeTable
                :value="anomalyDetailsStore.corruptedFields"
                table-style="height: 100%; width: 100%"
                :expanded-keys="anomalyDetailsStore.expandedRows"
              >
                <template #footer>
                  <div class="flex items-center justify-end">
                    <Button
                      :label="getLabelButton()"
                      :icon="getIconButton()"
                      @click="anomalyDetailsStore.checkStatus()"
                    />
                  </div>
                </template>
                <template #empty>
                  No issues found
                </template>
                <Column
                  v-if="windowIsTooSmall"
                  header-class="hidden"
                  field="object"
                  expander
                >
                  <template #body="{ node }">
                    <div class="flex justify-between items-center w-full">
                      <div
                        class="flex flex-col gap-2 w-full"
                      >
                        <div
                          v-if="node.data.field"
                          class="flex items-center"
                        >
                          <strong>{{ node.data.field }}</strong>
                          <i
                            class="pi pi-angle-right"
                          />
                          <p>{{ node.data.issue }}</p>
                        </div>
                        <strong v-else>
                          {{ node.data.object }}</strong>

                        <div
                          v-if="node.data.showChangeColumn !== undefined"
                          class="flex flex-row justify-center w-full"
                        >
                          <SelectButton
                            v-model="node.data.action"
                            option-label="name"
                            option-value="value"
                            class="flex justify-center w-full"
                            size="small"
                            option-disabled="disable"
                            :options="[{ name: 'Change Type', value: 'changeTypeFields', disable: !node.data.showChangeColumn}, { name: 'Remove', value: 'removeFields' }]"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                </Column>
                <Column
                  v-if="!windowIsTooSmall"
                  field="object"
                  header="Object"
                  expander
                />
                <Column
                  v-if="!windowIsTooSmall"
                  field="field"
                  header="Field"
                />
                <Column
                  v-if="!windowIsTooSmall"
                  field="issue"
                  header="Issue"
                />
                <Column
                  v-if="!windowIsTooSmall"
                  header="Actions"
                  header-class="flex flex-row justify-center w-full"
                >
                  <template #body="{node}">
                    <div
                      v-if="node.data.showChangeColumn !== undefined"
                      class="flex flex-row justify-center w-full"
                      style="min-width: 209px;"
                    >
                      <SelectButton
                        v-model="node.data.action"
                        option-label="name"
                        option-value="value"
                        class="flex justify-center w-full"
                        size="small"
                        option-disabled="disable"
                        :options="[{ name: 'Change Type', value: 'changeTypeFields', disable: !node.data.showChangeColumn}, { name: 'Remove', value: 'removeFields' }]"
                      />
                    </div>
                  </template>
                </Column>
              </TreeTable>
            </div>
          </TabPanel>
          <TabPanel
            :value="anomalyDetailsStore.errors[0].formattedEndDate ? '0' : '1'"
            class="h-full"
          >
            <div
              class="flex-1 overflow-y-auto px-0 -mx-4 md:mx-0 md:pb-4 rounded-lg bg-[--p-content-background] h-full"
              :class="{'py-2': !windowIsTooSmall}"
            >
              <DataTable
                selection-mode="single"
                :value="errors"
                table-style="height: 100%; width: 100%"
                scrollable
                scroll-height="flex"
                :virtual-scroller-options="{ itemSize: windowIsTooSmall ? 65 : 40, numToleratedItems: 50 }"
                @row-select="openDetailModal($event.data)"
              >
                <template #empty>
                  No errors found
                </template>
                <Column
                  v-if="windowIsTooSmall"
                  header-class="hidden"
                >
                  <template #body="{ data }">
                    <div class="flex justify-between items-center">
                      <div
                        class="flex flex-col gap-2"
                      >
                        <strong>
                          {{ data.object + ' - ' + data.operation }}</strong>
                        <div class="flex flex-row items-center gap-2 w-full">
                          <i
                            class="pi pi-clock"
                          />
                          <div class="flex flex-row items-center h-full">
                            {{ new Date(data.createddate).toLocaleString() }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </Column>
                <Column
                  v-if="!windowIsTooSmall"
                  field="createddate"
                  header="Created Date"
                  style="min-width: 170px; max-width: 170px; width: 170px; text-wrap: nowrap;"
                >
                  <template #body="{ data }">
                    {{ data.createddate.toLocaleString() }}
                  </template>
                </Column>
                <Column
                  v-if="!windowIsTooSmall"
                  field="object"
                  header="Object"
                />
                <Column
                  v-if="!windowIsTooSmall"
                  field="operation"
                  header="Operation"
                  style="max-width: 130px; width: 130px"
                />
              </DataTable>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>
<style lang="postcss">
.anomaly-timeline .p-timeline-event-opposite {
  flex: 0;
  width: 48px;
}

.p-treetable-footer {
  @apply border-none !important
}

.p-tabpanels {
  @apply h-full;
  @apply py-0 !important
}

</style>
